import { noop } from 'lodash';
import { useMemo } from 'react';

import {
   BoundService,
   CurrentHealthcareProviderBoundServiceQuery,
   ReceptionService,
} from '@dreryk/egabinet-patient-portal-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { getCustomFormattingContext, PriceFormatterOptions, priceRangeFormatter } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   JustifyContent,
   Table,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { boundServiceTranslationKeys, patientPortalTranslationKeys } from '../../translations';
import { HealthcareProviderPatientPortalBoundServiceRemove } from '../healthcareProviderPatientPortalBoundServiceRemove/HealthcareProviderPatientPortalBoundServiceRemove';

import { AddNewBoundServiceModalOpener } from './AddNewBoundServiceModalOpener';
import { CurrentHealthcareProviderBoundServiceTableEmptyStatePlaceholder } from './CurrentHealthcareProviderBoundServiceTableEmptyStatePlaceholder';

type BoundServiceTableColumnConfigurationOptions = {
   onBoundServiceRemoveSuccess: VoidFunction;
};

const useColumns = (options: BoundServiceTableColumnConfigurationOptions): TableColumnConfiguration<BoundService>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         patientPortalService: '50%',
         receptionService: '50%',
         price: '144px',
         options: '40px',
      });

      const priceFormatterContext = getCustomFormattingContext<PriceFormatterOptions>({
         locales: 'pl-PL',
         options: { currency: 'PLN' },
      });

      return [
         {
            name: 'patientPortalService',
            label: patientPortalTranslationKeys.boundServices.patientPortalService,
            width: columnsWidth.patientPortalService,
            accessor: (bean) => bean.patientPortalService.name,
            sortingDisabled: true,
         },
         {
            name: 'receptionService',
            label: patientPortalTranslationKeys.boundServices.receptionService,
            width: columnsWidth.receptionService,
            accessor: (bean) => bean.receptionService.name,
            sortingDisabled: true,
         },
         {
            name: 'price',
            label: patientPortalTranslationKeys.boundServices.price,
            width: columnsWidth.price,
            accessor: (bean) => bean.receptionService,
            formatter: ({ minPrice, maxPrice }: ReceptionService) => {
               return priceRangeFormatter(
                  { min: minPrice.asNumber(), max: maxPrice.asNumber() },
                  priceFormatterContext
               );
            },
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
         },
         {
            name: 'options',
            width: columnsWidth.options,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<BoundService>) => {
               return (
                  <HealthcareProviderPatientPortalBoundServiceRemove
                     patientPortalServiceName={bean.patientPortalService.name}
                     boundServiceId={bean.id}
                     receptionServiceName={bean.receptionService.name}
                     onBoundServiceRemoveSuccess={options.onBoundServiceRemoveSuccess}
                  />
               );
            },
            sortingDisabled: true,
         },
      ];
   }, [options]);
};

const useCurrentHealthcareProviderBoundServiceQuery = (): LazyLoadedDataConfigurationByCqrsQuery<BoundService> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<BoundService>(() => {
      return new CurrentHealthcareProviderBoundServiceQuery();
   });
};

export const CurrentHealthcareProviderBoundServiceTable = (): JSX.Element => {
   const dataConfiguration = useCurrentHealthcareProviderBoundServiceQuery();
   const columns = useColumns({ onBoundServiceRemoveSuccess: dataConfiguration.refetch });

   return (
      <Table
         columns={columns}
         dataConfiguration={dataConfiguration}
         testId={'currentHealthcareProviderBoundServiceTable'}
         placeholder={
            <CurrentHealthcareProviderBoundServiceTableEmptyStatePlaceholder
               onBoundServiceCreateSuccess={dataConfiguration.refetch}
            />
         }
         bottomElement={
            <AddNewBoundServiceModalOpener
               label={boundServiceTranslationKeys.addBound}
               onBoundServiceCreateSuccess={dataConfiguration.refetch}
               withLeadingIcon
            />
         }
         compact
         keyExtractor={(bean) => bean.patientPortalService.id}
      />
   );
};
